import { useMutation } from '@tanstack/react-query';
import giftClient from '../../../api/gift';
import { PendulumLogoType } from '../../../consts/pendulumLogo';
import { checkHasTransparentBackground } from '../../../utils/uploads';

interface UseUploadGiftPendulumLogoOptions {
  setUrl?: (url: string) => void;
  onSuccess?: (url: string) => void;
  onError?: (message?: string) => void;
  // A "full" logo fills the whole bob, so it is a normal opaque picture.
  pendulumLogoType?: PendulumLogoType;
}

const useUploadGiftPendulumLogo = ({
  setUrl,
  onSuccess,
  onError,
  pendulumLogoType,
}: UseUploadGiftPendulumLogoOptions) => {
  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['upload-gift-pendulum-logo'],
    mutationFn: (file: string) =>
      giftClient.uploadPendulumLogo(file, pendulumLogoType),
  });

  const handleFileSelection = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'image/png') {
      onError?.('Pendulum logo must be a PNG file');
      return;
    }

    const hasTransparentBackground =
      pendulumLogoType === 'full' ||
      (await checkHasTransparentBackground(file));
    if (!hasTransparentBackground) {
      onError?.(
        'Pendulum logo must be an icon on a transparent background (at least 10% of the image must be transparent)',
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setUrl?.(reader.result as string);
        const { url } = await mutateAsync(reader.result as string);
        setUrl?.(url);
        onSuccess?.(url);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to upload logo';
        onError?.(message);
      }
    };
    reader.onerror = () => {
      setUrl?.('');
      onError?.('Failed to read logo file');
    };
    reader.onabort = () => {
      setUrl?.('');
      onError?.('File reading was aborted');
    };
    reader.readAsDataURL(file);
  };

  return { handleFileSelection, isPending };
};

export default useUploadGiftPendulumLogo;
